// src/hooks/useConversations.ts
// Hook for managing persistent conversations with Mystara

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/browserClient';
import { useAuth } from './useAuth';
import type { 
  Conversation, 
  Message, 
  SessionType, 
  TransformationPhase,
  MystaraVoice 
} from '../types/mystara';

interface UseConversationsReturn {
  // Current conversation
  currentConversation: Conversation | null;
  messages: Message[];
  
  // All conversations
  conversations: Conversation[];
  
  // State
  loading: boolean;
  sending: boolean;
  error: string | null;
  
  // Actions
  startConversation: (sessionType?: SessionType) => Promise<string>;
  loadConversation: (conversationId: string) => Promise<void>;
  sendMessage: (content: string) => Promise<Message | null>;
  addMystaraMessage: (content: string, voiceUsed?: MystaraVoice) => Promise<void>;
  updateConversationTitle: (title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  
  // Refresh
  refreshConversations: () => Promise<void>;
}

export function useConversations(): UseConversationsReturn {
  const { user } = useAuth();
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all conversations for the user
  const fetchConversations = useCallback(async () => {
    if (!user?.id) {
      setConversations([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      const mapped: Conversation[] = (data || []).map(c => ({
        id: c.id,
        oderId: c.user_id,
        title: c.title,
        sessionType: c.session_type,
        messages: [], // Loaded separately
        phase: c.phase,
        summary: c.summary,
        createdAt: c.created_at,
        updatedAt: c.updated_at
      }));

      setConversations(mapped);
    } catch (err: any) {
      console.error('[useConversations] Error fetching:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Load a specific conversation with messages
  const loadConversation = useCallback(async (conversationId: string) => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch conversation
      const { data: convData, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .eq('user_id', user.id)
        .single();

      if (convError) throw convError;

      // Fetch messages
      const { data: msgData, error: msgError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (msgError) throw msgError;

      const conversation: Conversation = {
        id: convData.id,
        oderId: convData.user_id,
        title: convData.title,
        sessionType: convData.session_type,
        messages: [],
        phase: convData.phase,
        summary: convData.summary,
        createdAt: convData.created_at,
        updatedAt: convData.updated_at
      };

      const msgs: Message[] = (msgData || []).map(m => ({
        id: m.id,
        conversationId: m.conversation_id,
        role: m.role,
        content: m.content,
        voiceUsed: m.voice_used,
        timestamp: m.created_at,
        metadata: m.metadata
      }));

      setCurrentConversation(conversation);
      setMessages(msgs);
    } catch (err: any) {
      console.error('[useConversations] Error loading:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Start a new conversation
  const startConversation = useCallback(async (sessionType: SessionType = 'open'): Promise<string> => {
    if (!user?.id) throw new Error('Not authenticated');

    try {
      // Get user's current phase
      const { data: soulFile } = await supabase
        .from('soul_files')
        .select('current_phase')
        .eq('user_id', user.id)
        .single();

      const phase = soulFile?.current_phase || 'illuminate';

      // Create conversation
      const { data, error: createError } = await supabase
        .from('conversations')
        .insert({
          user_id: user.id,
          session_type: sessionType,
          phase,
          title: getDefaultTitle(sessionType)
        })
        .select()
        .single();

      if (createError) throw createError;

      const newConversation: Conversation = {
        id: data.id,
        oderId: data.user_id,
        title: data.title,
        sessionType: data.session_type,
        messages: [],
        phase: data.phase,
        summary: null,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      setCurrentConversation(newConversation);
      setMessages([]);
      
      // Refresh conversations list
      await fetchConversations();

      // Log activity for streak
      await supabase.from('activity_log').insert({
        user_id: user.id,
        activity_type: 'conversation',
        metadata: { session_type: sessionType }
      });

      return data.id;
    } catch (err: any) {
      console.error('[useConversations] Error starting:', err);
      setError(err.message);
      throw err;
    }
  }, [user?.id, fetchConversations]);

  // Send a user message
  const sendMessage = useCallback(async (content: string): Promise<Message | null> => {
    if (!user?.id || !currentConversation) return null;

    try {
      setSending(true);

      const { data, error: sendError } = await supabase
        .from('messages')
        .insert({
          conversation_id: currentConversation.id,
          user_id: user.id,
          role: 'user',
          content
        })
        .select()
        .single();

      if (sendError) throw sendError;

      const newMessage: Message = {
        id: data.id,
        conversationId: data.conversation_id,
        role: 'user',
        content: data.content,
        timestamp: data.created_at,
        metadata: data.metadata
      };

      setMessages(prev => [...prev, newMessage]);

      // Update conversation's updated_at
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', currentConversation.id);

      // Update title if it's the first message
      if (messages.length === 0) {
        const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
        await supabase
          .from('conversations')
          .update({ title })
          .eq('id', currentConversation.id);
        
        setCurrentConversation(prev => prev ? { ...prev, title } : null);
      }

      return newMessage;
    } catch (err: any) {
      console.error('[useConversations] Error sending:', err);
      setError(err.message);
      return null;
    } finally {
      setSending(false);
    }
  }, [user?.id, currentConversation, messages.length]);

  // Add Mystara's response
  const addMystaraMessage = useCallback(async (content: string, voiceUsed?: MystaraVoice) => {
    if (!user?.id || !currentConversation) return;

    try {
      const { data, error: addError } = await supabase
        .from('messages')
        .insert({
          conversation_id: currentConversation.id,
          user_id: user.id,
          role: 'mystara',
          content,
          voice_used: voiceUsed
        })
        .select()
        .single();

      if (addError) throw addError;

      const newMessage: Message = {
        id: data.id,
        conversationId: data.conversation_id,
        role: 'mystara',
        content: data.content,
        voiceUsed: data.voice_used,
        timestamp: data.created_at,
        metadata: data.metadata
      };

      setMessages(prev => [...prev, newMessage]);
    } catch (err: any) {
      console.error('[useConversations] Error adding Mystara message:', err);
      setError(err.message);
    }
  }, [user?.id, currentConversation]);

  // Update conversation title
  const updateConversationTitle = useCallback(async (title: string) => {
    if (!currentConversation) return;

    try {
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ title })
        .eq('id', currentConversation.id);

      if (updateError) throw updateError;

      setCurrentConversation(prev => prev ? { ...prev, title } : null);
      await fetchConversations();
    } catch (err: any) {
      console.error('[useConversations] Error updating title:', err);
      setError(err.message);
    }
  }, [currentConversation, fetchConversations]);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!user?.id) return;

    try {
      const { error: deleteError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Clear current if it was the deleted one
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }

      await fetchConversations();
    } catch (err: any) {
      console.error('[useConversations] Error deleting:', err);
      setError(err.message);
    }
  }, [user?.id, currentConversation, fetchConversations]);

  return {
    currentConversation,
    messages,
    conversations,
    loading,
    sending,
    error,
    startConversation,
    loadConversation,
    sendMessage,
    addMystaraMessage,
    updateConversationTitle,
    deleteConversation,
    refreshConversations: fetchConversations
  };
}

// Helper function for default titles
function getDefaultTitle(sessionType: SessionType): string {
  const titles: Record<SessionType, string> = {
    open: 'New conversation',
    coaching: 'Coaching session',
    ritual: 'Ritual',
    'card-pull': 'Card reading',
    'check-in': 'Check-in',
    onboarding: 'Welcome to Mystara'
  };
  return titles[sessionType] || 'New conversation';
}
